import React from "react";

// Terms of Service for FoodFax
// ----------------------------
// This component provides robust Terms of Service intended to protect
// FoodFax (OnePace LLC) from potential liabilities. It follows the
// same styling structure and approach as the Privacy component previously shown.

const Terms: React.FC = () => {
  return (
    <>
      <style>{styles}</style>
      <main className="terms-container">
        <article className="terms-content">
          <header className="terms-header">
            <h1 className="terms-title">Terms of Service</h1>
            <p className="last-updated">Effective Date: January 1, 2025</p>
          </header>

          {/* 1. Acceptance of Terms */}
          <section className="terms-section" aria-labelledby="acceptance">
            <h2 id="acceptance" className="section-title">
              1. Acceptance of Terms
            </h2>
            <p className="section-text">
              Welcome to FoodFax (the “Service”), owned and operated by OnePace
              LLC dba FoodFax App (“Company,” “we,” “us,” or “our”). By
              accessing or using this Service, you (“User” or “you”) confirm
              that you have read, understand, and agree to be bound by these
              Terms of Service. If you do not agree, please discontinue use
              immediately.
            </p>
          </section>

          {/* 2. Description of the Service */}
          <section
            className="terms-section"
            aria-labelledby="description-of-service"
          >
            <h2 id="description-of-service" className="section-title">
              2. Description of the Service
            </h2>
            <p className="section-text">
              FoodFax provides food ingredient analysis, dietary information,
              and related recommendations. All information provided is for
              informational purposes only and should not be taken as medical or
              nutritional advice. You acknowledge and agree that any reliance on
              such information is solely at your own risk.
            </p>
          </section>

          {/* 3. User Responsibilities */}
          <section
            className="terms-section"
            aria-labelledby="user-responsibilities"
          >
            <h2 id="user-responsibilities" className="section-title">
              3. User Responsibilities
            </h2>
            <ul className="terms-list">
              <li>
                You must be at least 18 years of age (or have the express
                permission and supervision of a parent or legal guardian) to use
                the Service.
              </li>
              <li>
                You agree to use the Service solely for lawful purposes and in
                compliance with all applicable local, state, national, and
                international laws.
              </li>
              <li>
                You agree not to upload, post, or transmit any content that is
                illegal, offensive, or infringes any third-party rights.
              </li>
              <li>
                You are solely responsible for maintaining the confidentiality
                of your account credentials and for all activities that occur
                under your account.
              </li>
            </ul>
          </section>

          {/* 4. Subscriptions and Payments */}
          <section className="terms-section" aria-labelledby="subscriptions">
            <h2 id="subscriptions" className="section-title">
              4. Subscriptions and Payments
            </h2>
            <p className="section-text">
              Certain features or functionalities may require a subscription or
              one-time payment. All fees are processed securely through Apple’s
              payment system. You agree to abide by Apple’s terms and conditions
              regarding subscription management and billing. We do not store
              your complete payment information, and any billing inquiries
              should be directed to Apple.
            </p>
          </section>

          {/* 5. Intellectual Property */}
          <section
            className="terms-section"
            aria-labelledby="intellectual-property"
          >
            <h2 id="intellectual-property" className="section-title">
              5. Intellectual Property
            </h2>
            <p className="section-text">
              All content, trademarks, service marks, logos, and other
              intellectual property displayed on the Service (collectively,
              “IP”) are owned or licensed by the Company. You agree not to
              reproduce, distribute, modify, or create derivative works of any
              of our IP without our express written consent.
            </p>
          </section>

          {/* 6. Disclaimer of Warranties */}
          <section className="terms-section" aria-labelledby="disclaimer">
            <h2 id="disclaimer" className="section-title">
              6. Disclaimer of Warranties
            </h2>
            <p className="section-text">
              THE SERVICE IS PROVIDED ON AN “AS IS” AND “AS AVAILABLE” BASIS,
              WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED,
              INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR
              NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE
              UNINTERRUPTED, ERROR-FREE, OR FREE FROM VIRUSES OR OTHER HARMFUL
              COMPONENTS.
            </p>
          </section>

          {/* 7. Limitation of Liability */}
          <section
            className="terms-section"
            aria-labelledby="limitation-of-liability"
          >
            <h2 id="limitation-of-liability" className="section-title">
              7. Limitation of Liability
            </h2>
            <p className="section-text">
              TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT
              SHALL THE COMPANY OR ITS AFFILIATES, OFFICERS, DIRECTORS,
              EMPLOYEES, AGENTS, OR LICENSORS BE LIABLE FOR ANY INDIRECT,
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING
              OUT OF OR RELATED TO YOUR USE OF THE SERVICE, EVEN IF ADVISED OF
              THE POSSIBILITY OF SUCH DAMAGES. OUR TOTAL LIABILITY FOR ANY CLAIM
              ARISING FROM OR RELATED TO THE SERVICE SHALL NOT EXCEED THE
              AMOUNT, IF ANY, PAID BY YOU TO US FOR THE SPECIFIC SERVICE GIVING
              RISE TO THE CLAIM.
            </p>
          </section>

          {/* 8. Indemnification */}
          <section className="terms-section" aria-labelledby="indemnification">
            <h2 id="indemnification" className="section-title">
              8. Indemnification
            </h2>
            <p className="section-text">
              You agree to defend, indemnify, and hold harmless the Company and
              its affiliates, officers, directors, employees, agents, and
              licensors from and against any claims, actions, or demands,
              liabilities, and settlements including, without limitation,
              reasonable legal fees, arising out of or related to your use of
              the Service or your violation of these Terms.
            </p>
          </section>

          {/* 9. Termination */}
          <section className="terms-section" aria-labelledby="termination">
            <h2 id="termination" className="section-title">
              9. Termination
            </h2>
            <p className="section-text">
              We may terminate or suspend your access to the Service, in whole
              or in part, at any time and for any or no reason, without notice
              or liability to you. Upon termination, your right to use the
              Service will immediately cease. Any provisions that by their
              nature should survive termination will survive.
            </p>
          </section>

          {/* 10. Governing Law & Dispute Resolution */}
          <section
            className="terms-section"
            aria-labelledby="governing-law-disputes"
          >
            <h2 id="governing-law-disputes" className="section-title">
              10. Governing Law &amp; Dispute Resolution
            </h2>
            <p className="section-text">
              These Terms shall be governed by and construed in accordance with
              the laws of the State of Texas, without regard to its conflict of
              laws principles. Any dispute arising under or relating to these
              Terms or the Service shall be resolved exclusively in the state or
              federal courts located in Travis County, Texas, and you consent to
              personal jurisdiction and venue in such courts.
            </p>
          </section>

          {/* 11. Changes to the Terms */}
          <section className="terms-section" aria-labelledby="changes-to-terms">
            <h2 id="changes-to-terms" className="section-title">
              11. Changes to the Terms
            </h2>
            <p className="section-text">
              We reserve the right, at our discretion, to change or modify these
              Terms at any time. Such changes or modifications shall become
              effective immediately upon posting. Your continued use of the
              Service following the posting of changes will constitute your
              acceptance of such changes.
            </p>
          </section>

          {/* 12. Contact Information */}
          <section className="terms-section" aria-labelledby="contact-info">
            <h2 id="contact-info" className="section-title">
              12. Contact Information
            </h2>
            <address className="contact-info">
              If you have any questions about these Terms, please contact us:
              <br />
              <a href="mailto:emma@foodfax.lol" className="contact-link">
                emma@foodfax.lol
              </a>
              <br />
              <strong>OnePace LLC dba FoodFax App</strong>
              <br />
              5900 Balcones Drive
              <br />
              Austin, Texas 78734
              <br />
              United States
            </address>
          </section>
        </article>
      </main>
    </>
  );
};

export default Terms;

// Same styling structure, but with "terms-" prefixes for the container, content, etc.
// for clarity. You can rename or merge these classes as needed in your final build.
const styles = `
.terms-container {
  max-width: 800px;
  margin: 2rem auto;
  padding: 0 1.5rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
  color: #1a1a1a;
  line-height: 1.6;
}

.terms-content {
  background: #ffffff;
  padding: 2.5rem;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
}

.terms-header {
  text-align: center;
  margin-bottom: 3rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #e5e7eb;
}

.terms-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.5rem;
  letter-spacing: -0.025em;
}

.last-updated {
  color: #6b7280;
  font-size: 0.875rem;
}

.terms-section {
  margin-bottom: 2.5rem;
  padding-bottom: 2.5rem;
  border-bottom: 1px solid #e5e7eb;
}

.terms-section:last-of-type {
  border-bottom: none;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 1.25rem;
  scroll-margin-top: 100px;
}

.section-text {
  color: #374151;
  margin-bottom: 1.25rem;
}

.terms-list {
  list-style: none;
  padding-left: 1.5rem;
  margin: 1rem 0;
}

.terms-list li {
  position: relative;
  padding-left: 1.5rem;
  margin-bottom: 0.75rem;
  color: #374151;
}

.terms-list li::before {
  content: "•";
  position: absolute;
  left: 0;
  color: #6b7280;
}

.contact-info {
  font-style: normal;
  line-height: 1.8;
  background: #f9fafb;
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 1rem;
}

.contact-link {
  color: #2563eb;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.15s ease;
}

.contact-link:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

@media (max-width: 768px) {
  .terms-container {
    margin: 1rem auto;
    padding: 0 1rem;
  }

  .terms-content {
    padding: 1.5rem;
  }

  .terms-title {
    font-size: 2rem;
  }

  .section-title {
    font-size: 1.25rem;
  }
}

@media print {
  .terms-container {
    margin: 0;
    padding: 0;
  }

  .terms-content {
    box-shadow: none;
    padding: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  * {
    animation: none !important;
    transition: none !important;
  }
}
`;
